import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { useQuery } from '@apollo/client';
import loadable from '@loadable/component';
import { Button, FlexBox, SvgComponent } from '@quikr/ui';
import Loader from '@quikr/ui/src/common/Button/Loader';
import storage from '@quikr/ui/src/helpers/Storage';
import { useStoreUtmParams } from '@quikr/ui/src/hooks';
import { apiWrapper, getSessionStorage } from '@quikr/ui/src/helpers/common';
import { DEFAULT_SELECTED_PACKID, SELECTED_PACKS } from '../../constants/bulkAd';
import { common } from '../../gql/common';
import BulkAdIcons from './assets/bulkAd-sprite.svg';

const Snackbar = loadable(() => import('@quikr/ui/src/common/Snackbar'), {
  ssr: false
});

const LoginModal = loadable(() => import('../../components/instantHire/LoginModal'), {
  ssr: false
});

const Wrapper = styled.div`
  background: ${({ theme }) => theme.colors.Neutral[300]};
  .mb24 {
    margin-bottom: 24px;
  }
  .mt12 {
    margin-top: 12px;
  }
  .gap6 {
    gap: 6px;
  }
  @media screen and (min-width: 768px) {
    .ldN {
      display: none;
    }
    padding: 110px 0;
  }
  @media screen and (max-width: 767px) {
    .xdN {
      display: none;
    }
  }
`;

const Container = styled(FlexBox.Container)`
  gap: 12px;
  padding: 0 12px;
  .cPrimary {
    color: ${({ theme }) => theme.colors.Primary};
  }
  @media screen and (max-width: 767px) {
    flex-direction: column;
    padding: 0;
  }
`;

const BulkAdContainer = styled.div`
  border-radius: 4px;
  background: #fff;
  box-shadow: 0px 0px 6px 0px rgba(0, 0, 0, 0.12);
  padding: 32px 16px 16px;
  max-width: 854px;
  .mb12 {
    margin-bottom: 12px;
  }
  @media screen and (max-width: 767px) {
    border-radius: 0;
    padding: 24px 12px;
    box-shadow: unset;
    .bulkAdIcon {
      height: 24px;
    }
    .paymentSection {
      flex-direction: column;
      align-items: flex-start;
      gap: 24px;
    }
    .paymentSection ul {
      margin-bottom: 10px;
    }
  }
`;

const Packs = styled.div`
  padding: 24px 0;
  gap: 16px 12px;
  display: grid;
  align-items: end;
  text-align: -webkit-right;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  .packContent {
    border-radius: 4px;
    border: 1px solid ${({ theme }) => theme.colors.Neutral[100]};
    & > div {
      padding: 12px;
    }
    & > div:nth-child(2) {
      background: ${({ theme }) => theme.colors.Neutral[400]};
      border-top: 1px solid ${({ theme }) => theme.colors.Neutral[100]};
    }
    .subText {
      color: ${({ theme }) => theme.colors.TextGamma};
    }
  }
  .selected.packContent {
    border-color: ${({ theme }) => theme.colors.Primary};
    & > div:nth-child(2) {
      background: ${({ theme }) => theme.colors.Primary};
      border-top-color: ${({ theme }) => theme.colors.Primary};
      .buyBtn {
        color: #fff;
      }
      .quantitySelector {
        border-color: ${({ theme }) => theme.colors.Primary};
      }
    }
  }
  .gap12 {
    gap: 12px;
  }
  .mb6 {
    margin-bottom: 6px;
  }
  @media screen and (max-width: 767px) {
    grid-template-columns: 1fr;
    .packContent {
      & > div {
        padding: 8px;
      }
      & > div:nth-child(1) {
        gap: 4px;
      }
      .subText {
        font-size: 9px;
        font-weight: 400;
        line-height: normal;
      }
    }
  }
`;

const PackQuantitySelector = styled.div`
  background: #fff;
  border-radius: 2px;
  border: 1px solid #000;
  color: #000;
  .quantity__minus,
  .quantity__plus {
    padding: 0 0 1px;
  }
  .quantity__minus {
    padding-left: 10px;
  }
  .quantity__plus {
    padding-right: 10px;
  }
  .quantity__input {
    padding: 0;
    border: none;
    width: 30px;
  }
`;

const Disclaimer = styled.div`
  background: ${({ theme }) => theme.colors.Neutral[300]};
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.TextGamma};
  padding: 10px 18px;
  margin: 0 -10px -10px;
  align-items: center;
  a {
    color: ${({ theme }) => theme.colors.TextBeta};
  }
  @media screen and (max-width: 767px) {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
    padding: 16px 12px;
  }
`;

const Badge = styled.div`
  border-radius: 32px;
  color: #fff;
  padding: 2px 8px;
  font-size: 9px;
  &.bestPlan {
    width: 72px;
    margin-bottom: 8px;
    background: ${({ theme }) => theme.colors.Secondary};
  }
  &.costProfile {
    background: #fceb42;
    color: ${({ theme }) => theme.colors.TextAlpha};
  }
  @media screen and (max-width: 767px) {
    padding: 2px 6px;
  }
`;

const PayButton = styled(Button)`
  min-width: 266px;
  max-width: 280px;
  padding: 10px 20px;
  height: unset;
  @media screen and (max-width: 767px) {
    position: fixed;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    max-width: 100%;
    border: 0;
    border-radius: 0;
    z-index: 1;
    background: #fbe710;

    font-size: 18px;
    font-weight: 600;
    line-height: 22px;

    span {
      font-size: 14px;
      line-height: 17px;
    }
  }
`;

const Loading = styled.div`
  padding: 32px;
`;

const JobsBulkAd = () => {
  const { data: { user } = {}, error: errorCommon, loading: loadingCommon } = useQuery(common);
  const [packs, setPacks] = useState([]);
  const [selectedPack, setSelectedPack] = useState({
    totalPrice: 0
  });
  const [loading, setLoading] = useState(false);
  const [packLoading, setPackLoading] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState({});
  const [showLoginModal, setShowLoginModal] = useState(false);

  useStoreUtmParams();

  const utmParams = useRef();

  const methods = useForm({
    mode: 'onChange'
  });

  const { register, setValue } = methods;

  useEffect(() => {
    gtag('event', 'page_view', {
      page_title: 'JobsBulkAd_Landing',
      event_category: 'Jobs'
    });

    utmParams.current = getSessionStorage('utm_params');
    setPackLoading(true);
    apiWrapper({
      // eslint-disable-next-line no-undef
      url: `${__WP_CLIENT_GQL_ENDPOINT}/o/bulkAd/getpacks`,
      headers: {
        'Content-Type': 'application/json'
      }
    })
      .then(response => {
        const packsResp = response.data || [];
        if (packsResp.length > 0) {
          setPacks(packsResp);
          const defaultPackPrice = packsResp.filter(
            pack => pack.packId === DEFAULT_SELECTED_PACKID
          )[0].price;
          setSelectedPack(prev => ({
            [DEFAULT_SELECTED_PACKID]: {
              price: defaultPackPrice,
              quantity: 1
            },
            totalPrice: defaultPackPrice
          }));
        }
      })
      .catch(errors => {
        console.error(errors);
        setSnackbarMessage({
          type: 'error',
          message: 'Some unknown error occurred. Please try again after some time'
        });
      })
      .finally(() => {
        setPackLoading(false);
      });
  }, []);

  const handlePackSelect = (e, pack) => {
    setSelectedPack(prev => {
      // unselect
      if (Object.keys(prev).includes(pack.packId.toString())) {
        const newSelectPackArr = Object.assign({}, prev);
        newSelectPackArr.totalPrice -=
          newSelectPackArr[pack.packId].price * newSelectPackArr[pack.packId].quantity;
        delete newSelectPackArr[pack.packId];
        setValue(`quantity${pack.packId}`, 0);
        return newSelectPackArr;
      }
      // select
      setValue(`quantity${pack.packId}`, 1);
      return {
        ...prev,
        [pack.packId]: {
          price: pack.price,
          quantity: 1
        },
        totalPrice: prev.totalPrice + pack.price
      };
    });
  };

  const handleQuantitySelectorClick = (e, operator, pack) => {
    e.stopPropagation();
    const selectedPacksArr = Object.keys(selectedPack);
    let value = (selectedPack[pack.packId] && selectedPack[pack.packId].quantity) || 0;
    if (operator === 'increment') {
      value++;
    } else if (operator === 'decrement') {
      if (value === 0) return;
      value--;
    }
    if (value !== 0 && !selectedPacksArr.includes(pack.packId.toString())) {
      handlePackSelect(e, pack);
      return;
    } else if (value === 0) {
      handlePackSelect(e, pack);
      return;
    }

    setValue(`quantity${pack.packId}`, value);
    setSelectedPack(prev => ({
      ...prev,
      [pack.packId]: {
        price: pack.price,
        quantity: value
      },
      totalPrice:
        prev.totalPrice - prev[pack.packId].price * prev[pack.packId].quantity + pack.price * value
    }));
  };

  const toggleLoginModal = () => {
    setShowLoginModal(prev => !prev);
  };

  const onSubmit = useCallback(
    async dataOrEvent => {
      if (dataOrEvent && typeof dataOrEvent.preventDefault !== 'undefined') {
        dataOrEvent.preventDefault();
      }
      const loggedIn = Boolean(user);
      if (!loggedIn) {
        // store data in local storage
        await storage.set(SELECTED_PACKS, JSON.stringify(selectedPack));
        toggleLoginModal();
        return;
      }
      setLoading(true);
      apiWrapper({
        // eslint-disable-next-line no-undef
        url: `${__WP_CLIENT_GQL_ENDPOINT}/u/bulkad/submit`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: {
          selectedPacks: selectedPack.totalPrice === 0 ? dataOrEvent : selectedPack,
          utmParams: utmParams.current
        }
      })
        .then(response => {
          if (response.paymentUrl) {
            window.location = response.paymentUrl;
          } else {
            setSnackbarMessage({
              type: 'error',
              message: 'Some unknown error occurred. Please try again after some time'
            });
          }
        })
        .catch(errors => {
          console.error(errors);
          setSnackbarMessage({
            type: 'error',
            message: 'Some unknown error occurred. Please try again after some time'
          });
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [selectedPack, user]
  );

  useEffect(() => {
    if (loadingCommon) return;
    const verifyAndSubmitData = async () => {
      const data = await storage.get(SELECTED_PACKS);
      const hasData = data !== 'undefined' && data !== undefined;
      const selectedPacks = hasData ? JSON.parse(data) : {};
      if (Object.keys(selectedPacks).length === 0) {
        return;
      }
      setSelectedPack(selectedPacks);
      onSubmit(selectedPacks);
    };
    const params = new URLSearchParams(window.location.search);
    const action = params.get('action');
    const removeHash = () => {
      params.delete('action');
      let url = window.location.pathname;
      url += params.toString() ? `?${params.toString()}` : '';

      window.history.replaceState('', document.title, url);
    };
    if (user && user.user && user.user.id) {
      if (!(action && action === btoa(user.user.id))) {
        action && storage.delete(SELECTED_PACKS);
        removeHash();
        return;
      }
      verifyAndSubmitData();
      removeHash();
    }
  }, [loadingCommon, user, onSubmit]);

  const loginSuccessCallback = response => {
    const { userid } = response;
    window.location.search = `?action=${btoa(userid)}`;
  };

  if (loadingCommon || errorCommon) return null;

  return (
    <Wrapper>
      <Container className="dF aiFS jcC">
        <BulkAdContainer className="w100 taC">
          <SvgComponent
            className="bulkAdIcon"
            svgSprite={BulkAdIcons}
            width={212}
            height={41}
            id="bulkAd"
          />
          <p className="bt2 mt12 xdN">
            To continue posting more Ads and receive Unlimited Applications from job seekers, please
            upgrade to our <span className="fwM cPrimary">“BULK AD PACK”</span>.
          </p>
          {packLoading ? (
            <Loading>
              <Loader />
            </Loading>
          ) : (
            <form className="mb24" onSubmit={onSubmit} noValidate>
              <Packs>
                {packs.map((pack, idx, arr) => {
                  const selectedPacksArr = Object.keys(selectedPack);
                  return (
                    <div key={idx}>
                      {pack.packId === DEFAULT_SELECTED_PACKID && (
                        <Badge className="bestPlan fwB taC mb8">BEST PLAN</Badge>
                      )}
                      <div
                        className={`packContent dF fdC jcSB oH cP taL ${
                          selectedPacksArr.includes(pack.packId.toString()) ? 'selected' : ''
                        }`}
                        key={pack.packId}
                      >
                        <div className="dFA jcSB">
                          <div>
                            <p className="bt2 fwM mb6">
                              <span className="h3 fwB">{pack.maxFreeAdLimit}</span> AD CREDITS
                            </p>
                            <span className="bt3 subText">{pack.duration} Days</span>
                          </div>
                          <div className="taR">
                            <p className="bt2 fwM mb6">
                              <span className="bt2">₹</span>
                              {pack.price}
                            </p>
                            <Badge className="costProfile">
                              Cost/Ad <span className="fwM bt3">₹{pack.costPerAd}</span>
                            </Badge>
                          </div>
                        </div>
                        <div className="dF jcSB">
                          <div className="dF gap12">
                            <PackQuantitySelector className="bt3 dFA jcC quantitySelector">
                              <div
                                className="quantity__minus"
                                onClick={e => handleQuantitySelectorClick(e, 'decrement', pack)}
                                role="button"
                                tabIndex="-1"
                                onKeyPress={e => handleQuantitySelectorClick(e, 'decrement', pack)}
                              >
                                &#8211;
                              </div>
                              <input
                                {...register(`quantity${pack.packId}`)}
                                type="text"
                                className="quantity__input taC"
                                value={
                                  selectedPacksArr.includes(pack.packId.toString())
                                    ? selectedPack[pack.packId].quantity || 1
                                    : '0'
                                }
                                disabled
                              />
                              <div
                                className="quantity__plus"
                                onClick={e => handleQuantitySelectorClick(e, 'increment', pack)}
                                role="button"
                                tabIndex="-1"
                                onKeyPress={e => handleQuantitySelectorClick(e, 'increment', pack)}
                              >
                                &#43;
                              </div>
                            </PackQuantitySelector>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </Packs>
              {Array.isArray(packs) && packs.length > 0 && (
                <div className="dF aiFE jcSB paymentSection">
                  <div className="taL">
                    <p className="bt2 fwM mb12">Benefits of BULK AD PACK:</p>
                    <ul className="bt3">
                      <li className="mb12 dFA gap6">
                        {/* eslint-disable-next-line max-len */}
                        <SvgComponent
                          svgSprite={BulkAdIcons}
                          width={14}
                          height={14}
                          id="check"
                        />{' '}
                        Unlock unlimited applications till the validity.
                      </li>
                      <li className="dFA gap6">
                        {/* eslint-disable-next-line max-len */}
                        <SvgComponent
                          svgSprite={BulkAdIcons}
                          width={14}
                          height={14}
                          id="check"
                        />{' '}
                        *No limit on daily ad postings.
                      </li>
                    </ul>
                  </div>
                  <PayButton
                    btnType="postAd"
                    type="submit"
                    disabled={selectedPack.totalPrice === 0}
                    loading={loading}
                  >
                    Pay <span className="bt2">₹</span>
                    {selectedPack.totalPrice}
                  </PayButton>
                </div>
              )}
            </form>
          )}
          <Disclaimer className="dF jcFE xdN">
            <p className="bt3">
              Need help? Call us:{' '}
              <a className="fwB" href="tel:080-66095795">
                080-66095795
              </a>
            </p>
          </Disclaimer>
        </BulkAdContainer>
      </Container>
      {showLoginModal && (
        <LoginModal
          show={showLoginModal}
          setShow={toggleLoginModal}
          loginSuccessCallback={loginSuccessCallback}
        />
      )}
      {snackbarMessage.message && (
        <Snackbar
          message={snackbarMessage.message}
          type={snackbarMessage.type}
          timeout={5000}
          animationClass="Slide"
          onClose={() => setSnackbarMessage({})}
        />
      )}
    </Wrapper>
  );
};

export default JobsBulkAd;
